// src/components/PairingSelector.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePairing } from '../context/PairingContext';

const ALLOWED_ROLES = ['mentor', 'mentee'];

/**
 * Kompakter Dropdown fuer Multi-Pairing-Auswahl.
 *
 * Sichtbar nur wenn:
 * - User hat Rolle 'mentor' oder 'mentee'
 * - Mindestens 2 Pairings vorhanden
 * Sonst rendert die Komponente null (keine UI).
 *
 * Styling: passt zu NavBar (dunkler Hintergrund, helle Schrift).
 */
export default function PairingSelector({ user }) {
  const { t } = useTranslation();
  const { pairings, selectedMatchingId, setSelectedPairing } = usePairing();

  // Rolle des Users bestimmen (kompatibel mit NavBar-Pattern)
  const role = user?.role || user?.mentoring_role;
  const isAllowedRole = role && ALLOWED_ROLES.includes(role);

  // Sichtbarkeits-Bedingungen
  if (!isAllowedRole) return null;
  if (!pairings || pairings.length < 2) return null;

  const handleChange = (e) => {
    const value = e.target.value;
    setSelectedPairing(value ? Number(value) : null);
  };

  // Label: "Paarung" oder i18n-Key. Fallback fuer den Fall, dass kein i18n-Key existiert.
  const labelText = t('pairingSelector.label', { defaultValue: 'Paarung' });

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '0 12px',
      color: '#ccc',
      fontSize: '14px',
    },
    label: {
      whiteSpace: 'nowrap',
      fontSize: '13px',
      color: '#aaa',
    },
    select: {
      background: '#333',
      color: '#fff',
      border: '1px solid #555',
      borderRadius: '4px',
      padding: '4px 8px',
      fontSize: '14px',
      cursor: 'pointer',
      maxWidth: '220px',
    },
  };

  return (
    <div style={styles.container} data-testid="pairing-selector">
      <span style={styles.label}>{labelText}:</span>
      <select
        value={selectedMatchingId || ''}
        onChange={handleChange}
        style={styles.select}
        aria-label={labelText}
      >
        {pairings.map((p) => {
          const label = p.partner_name?.trim()
            || `#${p.matching_id}`;
          const roleHint = p.my_role === 'mentor' ? '(Mentee)' : '(Mentor)';
          return (
            <option key={p.matching_id} value={p.matching_id}>
              {label} {roleHint}
            </option>
          );
        })}
      </select>
    </div>
  );
}
